import React, { useState } from "react";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { timestamp } from "../../firebase/config";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Stack, TextField } from "@mui/material";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { useFirestore } from "../../hooks/useFirestore";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import { categories } from "../../pages/create/Create";
import "./StackableAssignment.css";

const StackableAssignment = ({ parentProject, onClose, onSuccess }) => {
  const { user } = useAuthContext();
  const { addDocument, response } = useFirestore("projects");
  
  // Only pre-fill category from parent project
  const [title, setTitle] = useState("");
  const [assignmentDetails, setAssignmentDetails] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [liveDate, setLiveDate] = useState(null);
  const [assignedCategory, setAssignedCategory] = useState(parentProject.category);
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsPending(true);

    if (!title) {
      setError("Title Field Required");
      setIsPending(false);
      return;
    }
    if (!assignmentDetails) {
      setError("Assignment Field Required");
      setIsPending(false);
      return;
    }
    if (!dueDate) {
      setError("Due Date Field Required");
      setIsPending(false);
      return;
    }
    if (!assignedCategory) {
      setError("Category Field Required");
      setIsPending(false);
      return;
    }

    const createdBy = {
      displayName: user.displayName,
      photoURL: user.photoURL,
      id: user.uid,
    };

    const stackedProject = {
      title,
      details: assignmentDetails,
      category: assignedCategory,
      dueDate: timestamp.fromDate(dueDate),
      liveDate: liveDate ? timestamp.fromDate(liveDate) : null,
      completedDate: "",
      comments: [],
      completed: false,
      createdBy,
      grade: null,
      parentProjectId: parentProject.id, // Link to parent
      isStacked: true, // Flag to identify stacked assignments
    };

    await addDocument(stackedProject);
    
    if (!response.error) {
      // Increment the parent project's stackedCount
      const db = getFirestore();
      const parentRef = doc(db, 'projects', parentProject.id);
      await updateDoc(parentRef, {
        stackedCount: (parentProject.stackedCount || 0) + 1
      });
      
      setIsPending(false);
      onSuccess && onSuccess();
    } else {
      setError(response.error);
      setIsPending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Stack Assignment on "{parentProject.title}"</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error">{error}</div>}
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3} sx={{ width: "auto", padding: "20px" }}>
              <TextField
                id="outlined-required"
                label="Assignment Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
              />

              <TextField
                id="outlined-textarea"
                label="Assignment Details"
                multiline
                rows={4}
                value={assignmentDetails}
                onChange={(e) => setAssignmentDetails(e.target.value)}
                fullWidth
              />

              <DatePicker
                label="Set due date"
                value={dueDate}
                onChange={(newDate) => setDueDate(newDate)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />

              <DatePicker
                label="Set assignment live date (optional)"
                value={liveDate}
                onChange={(newDate) => setLiveDate(newDate)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />

              <FormControl fullWidth>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  id="category-select"
                  value={assignedCategory}
                  label="Category"
                  onChange={(event) => setAssignedCategory(event.target.value)}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.label}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-cancel" 
                  onClick={onClose}
                  disabled={isPending}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isPending}
                >
                  {isPending ? "Creating..." : "Create Stacked Assignment"}
                </button>
              </div>
            </Stack>
          </LocalizationProvider>
        </form>
      </div>
    </div>
  );
};

export default StackableAssignment;